/**
 * TASK-978 public help library.
 * Voice, Sign in, Car owner, Your shop, Plans, and Visibility articles are the steps a person can follow.
 */

export const HELP_RAIL_STORAGE_KEY = "help_library_rail_mode";

function article(id, title, summary, steps, extra = {}) {
  return { id, title, summary, steps, pending: steps.length === 0, ...extra };
}

function pending(id, title, summary) {
  return article(id, title, summary, []);
}

export const HELP_TOPICS = [
  {
    id: "sign-in",
    title: "Sign in and sign up",
    articles: [
      article(
        "shop-vs-owner",
        "Shop login and car-owner login",
        "Which login to use, and what each one is for.",
        [
          "**[Log in as a car owner](/login?role=User)** This opens Login with Car Owner already selected.",
          "**[Log in as a shop](/login?role=Mechanic)** This opens Login with Autoshop already selected. A shop owner and a mechanic both use this.",
          "A car owner uses Car Owner. A shop owner and a mechanic use Autoshop. These are separate accounts. The same email can be on both.",
          "The wrong choice shows Invalid email or password. That is the same message as a wrong password. Switch to the other choice and try the password again.",
          "Enter the email and password for that account.",
          "Finish the check above Log In. Log In stays off until that check is done.",
          "If the account’s email is not verified yet, the page says to verify it and shows Resend Verification Email. Check the inbox and the spam folder.",
        ],
        { relatedId: "sign-up", relatedLabel: "Sign up" },
      ),
      article(
        "sign-up",
        "Sign up",
        "Create a car-owner account or a shop account.",
        [
          "On Login, choose Sign Up. The question is: Are you a car owner or auto shop owner? Choose Car Owner or Auto Shop Owner.",
          "Auto Shop Owner, Mechanic, and Autoshop are the same shop account. The form is titled Auto Shop Sign Up. After the email is verified, that account signs in with Autoshop.",
          "The password must be at least 8 characters and include a number, an uppercase letter, and a special character: ! @ # $ % ^ & * ( ) _ - + =.",
          "**[Sign up as a car owner](/signup?role=User)** Enter first name, last name, email, phone, and password, then Next. Enter the street, city, state, and ZIP, then Next. A VIN is optional. Agree to the privacy policy and terms of service, finish the check, and choose Sign Up.",
          "**[Sign up as a shop](/signup?role=Mechanic)** Enter first name, last name, email, phone, and password, then Next. Under Select Shop to Claim, the shop name is optional and the ZIP is required. Search, then Claim Shop. A shop marked Already Claimed cannot be taken. If the shop is not in the list, choose Search Again.",
          "If a shop sent an invite, open that link instead of searching. A mechanic invite still needs the shop owner to approve the account.",
          "After a claimed shop is approved, the owner adds a credit card on file before sending quotes. The free plan can stay. The card is for later platform fees.",
          "After Sign Up, verify the email. The link lasts 7 days. Check spam. Login stays closed until that email is verified.",
        ],
        { relatedId: "where-login-lands", relatedLabel: "Where each login lands" },
      ),
      article(
        "reset-password",
        "Reset a password",
        "Get a new password from the login screen.",
        [
          "**[Reset a password](/forgot-password)** Enter the email on that page and choose Send Reset Link.",
          "If that email is not an account, the page says Email not found.",
          "If that email is both a car-owner account and a shop account, the page asks you to pick one. User is the car-owner account. Mechanic is the shop account. Pick one, then send the link again.",
          "The email subject is Reset Your Autoshops.com Password. The link lasts 15 minutes. Check spam. If it expires, request a new link.",
          "On the link, enter a new password and confirm it. It must be at least 8 characters, and the two entries must match.",
          "Choose Reset Password. The page returns to Login. Sign in with the same choice you reset: Car Owner or Autoshop.",
        ],
        { relatedId: "shop-vs-owner", relatedLabel: "Shop login and car-owner login" },
      ),
      article(
        "change-email",
        "Change your email",
        "Move the account to a new email after you are signed in.",
        [
          "Sign in first. A car owner uses Car Owner. A shop owner or mechanic uses Autoshop.",
          "**[Open your profile](/profile)** Next to Email, choose the envelope. The tip says Change Email.",
          "**[Open Mechanic Profile](/mechanic-profile)** Under Mechanic Info, next to Email, choose the envelope. The tip says Change Email.",
          "The dialog is Change Email Address. It shows the current email. Enter the new email and choose Send Verification. The new email must be different, and it cannot already be on another account of the same kind.",
          "The new inbox gets Verify Your New Email Address. Open that link to confirm the change. Check spam. If the link has expired, request the change again from the envelope.",
          "The current inbox gets Email Change Request. That message can cancel the change.",
          "After the new email is confirmed, you are signed out. Log in with the new email.",
        ],
        { relatedId: "shop-vs-owner", relatedLabel: "Shop login and car-owner login" },
      ),
      article(
        "where-login-lands",
        "Where each login lands",
        "The page that opens after a shop or a car owner signs in.",
        [
          "Car Owner opens a page that says Hello and your first name. Your profile and your cars are there.",
          "Autoshop opens Mechanic Profile. A shop owner and a mechanic both land on that page.",
          "On Mechanic Profile, Go to Dashboard opens the shop’s requests and work.",
          "If another page sent you to Login, you return to that page after you sign in.",
        ],
        { relatedId: "shop-vs-owner", relatedLabel: "Shop login and car-owner login" },
      ),
    ],
  },
  {
    id: "car-owners",
    title: "Car owners",
    articles: [
      article(
        "find-a-shop",
        "Find a shop",
        "Search for a shop near a ZIP code.",
        [
          "**[Shops Near Me](/google-wrapper)** No sign-in is required.",
          "Enter a ZIP Code, choose a Search Radius, and choose Search.",
          "The map and the list show shops near that ZIP.",
          "Visit Site opens that shop’s own website when the shop has one.",
        ],
        { relatedId: "request-service", relatedLabel: "Request service" },
      ),
      article(
        "profile-and-cars",
        "Your profile and your cars",
        "Keep your account and the cars on it up to date.",
        [
          "**[Log in as a car owner](/login?role=User)** A shop login does not open this profile.",
          "**[Open your profile](/profile)** The page says Hello and your first name.",
          "My Vehicles lists the cars. Choose a car to select it.",
          "Add New Vehicle, enter the car, and choose Save Vehicle.",
        ],
        { relatedId: "request-service", relatedLabel: "Request service" },
      ),
      article(
        "request-service",
        "Request service",
        "Ask a shop for work. A request does not always come back as a quote.",
        [
          "**[Log in as a car owner](/login?role=User)** Then open your profile.",
          "**[Open your profile](/profile)** Open Schedule a Service. Add a car under My Vehicles first. The form says no cars were found until one is saved.",
          "If the form asks how you want to find a shop, pick one. Request Quote from Specific Shop sends it to the shop you select. Broadcast to Nearby Shops shows it to shops in the radius, and each can send one quote. Competitive Bidding lets shops send more than one bid.",
          "Enter the ZIP and radius when the form asks. Choose Select Services and Select Your Car. Describe the issue or request.",
          "Add Time Slot adds up to 3 times you can be there. A slot has to be at least 30 minutes from now and at least 1 hour long. On a broadcast, drop-off days for a week are optional.",
          "Choose Submit Request. It appears under Service Requests. Until a shop sends a price, it says Waiting for shop to send a quote.",
          "A request is not a quote. Some jobs cannot be priced from the request alone. The shop may need a conversation, or it may need to inspect the car, before it can give an accurate price. Those details are not on the form.",
          "Use Message on the request for that conversation. When a quote arrives, Accept or Decline it.",
        ],
        { relatedId: "find-a-shop", relatedLabel: "Find a shop" },
      ),
      article(
        "diagnostic-codes",
        "Diagnostic codes",
        "Look up a code from the car.",
        [
          "**[Diagnostic Code](/obd)** The page is OBD2 Code Lookup. No sign-in is required to look up a code.",
          "Enter a code, such as P0100, and choose Check Code. The page shows the code and a description.",
          "A code is a clue from the car. It is not a quote, and it does not name the repair.",
          "A signed-in car owner can save the search to a car and mark the code active. Saved codes are on the profile under Diagnostic Code History.",
          "The code comes from an OBD2 scanner on a car made in 1996 or later. The port is usually under the dashboard, near the steering wheel. Some parts stores will scan the car.",
          "Descriptions are supplied by a third-party provider. Accuracy is not Autoshops’ responsibility.",
          "**[Send a message](/contact?subject=Inaccurate%20diagnostic%20code)** If a description looks wrong, use this link. Include the code and what looks wrong.",
        ],
        { relatedId: "request-service", relatedLabel: "Request service" },
      ),
    ],
  },
  {
    id: "your-shop",
    title: "Your shop",
    articles: [
      article(
        "shop-profile",
        "Shop profile, hours, and location",
        "The public facts customers see about the shop.",
        [
          "**[Log in as a shop](/login?role=Mechanic)** A car-owner login does not open the shop.",
          "**[Open Mechanic Profile](/mechanic-profile)** Choose Go to Dashboard.",
          "**[Open the dashboard](/mechanic-dashboard)** Shop Overview shows the name, address, and phone. Business Hours lists each day, or says that no hours are set.",
          "Those are the facts customers see when they find the shop. A line under the hours says where they came from, often Google Places.",
          "**[Send a message](/contact?subject=Shop%20profile%20change)** To change the public name, address, phone, or hours, use this link and say what should change.",
        ],
        { relatedId: "service-requests", relatedLabel: "Service requests and quotes" },
      ),
      article(
        "service-requests",
        "Service requests and quotes",
        "Read a request, send a quote, and wait for the customer to accept.",
        [
          "**[Open the dashboard](/mechanic-dashboard)** Open Service Requests.",
          "Direct Requests are jobs aimed at this shop. Broadcast Requests are jobs sent to shops nearby. Broadcast is on the screen only when that choice is open for the shop.",
          "Read the car, the services, and the customer note. Some jobs cannot be priced from that alone. The shop may need a conversation, or it may need to see the car, before an accurate price. Use Start Conversation, or Reply if a thread is already open.",
          "When the price is ready, enter Bid Price ($) and choose Submit Bid. The shop has to be Active, and a credit card is on file before quotes. If Submit Bid stays off, the tip on the button says why.",
          "The customer accepts or declines. After they accept, the request says Customer Accepted Your Quote. The quote is a price. The visit is confirmed when the customer accepts.",
        ],
        { relatedId: "direct-requests", relatedLabel: "Direct requests" },
      ),
      article(
        "direct-requests",
        "Direct requests",
        "A job aimed at this shop, from the website or from a phone call.",
        [
          "**[Open the dashboard](/mechanic-dashboard)** Open Service Requests, then Direct Requests.",
          "A customer who chose this shop on the website lands here. A phone call that starts a request lands here too.",
          "Quote both the same way. Enter Bid Price ($) and choose Submit Bid. Use Start Conversation when the request is not enough to price the job.",
          "The call does not book the visit and does not set a price. The calendar row is written after the shop sends the quote and the customer accepts.",
        ],
        { relatedId: "direct-request", relatedLabel: "A call that starts a Direct request" },
      ),
      article(
        "calendar",
        "Calendar",
        "The shop calendar on autoshops.com.",
        [
          "**[Open the calendar](/calendar)** The dashboard also has a Calendar button.",
          "Month, Week, and Day change the view. The chip shows the shop’s time zone.",
          "Visits the shop books, and visits a customer has accepted, show on this calendar. This is the shop’s own calendar.",
          "Connecting Google, Outlook, CalDAV, or shop software is a separate step. The Autoshops calendar still holds the appointments if that connection is off.",
        ],
        { relatedId: "connect-calendar", relatedLabel: "Connect Google, Outlook, CalDAV, or shop software" },
      ),
      article(
        "connect-calendar",
        "Connect Google, Outlook, CalDAV, or shop software",
        "The Calendar upgrade is $25 a month for Google, Outlook, CalDAV, and shop software.",
        [
          "**[Manage Your Subscription](/pricing)** Calendar upgrade is $25 a month. It requires Voice Base. Buy it on this page. Google, Outlook, CalDAV, and shop software are this same $25.",
          "**[Open the calendar](/calendar)** An owner opens Calendar settings, the gear.",
          "Choose Calendar provider. Google Calendar is the connection that turns on. A provider marked coming soon is listed and is not connected.",
          "Save settings, then Connect Google Calendar. After it connects, Sync now keeps the Autoshops calendar and Google aligned.",
          "Shop software is the same $25. That includes Tekmetric, Shopmonkey, Mitchell, Shop-Ware, AutoLeap, RepairShopr, R.O. Writer, and DaySmart.",
          "The first shop on a given software works with Autoshops while that connection is built, so later shops on the same software can use it. The extra setup time adds no charge. The price stays $25 a month.",
          "**[Send a message](/contact?subject=Shop%20software%20calendar)** Use this link to start a shop-software connection. Say which software the shop uses. Ask a question on this page does the same thing.",
          "Buffer is the gap between appointments. Slot length is the grid used to find an open time. It does not force every job to last that long.",
          "If the connection is unhealthy, appointments can still be made on Autoshops. Reconnect Google to resume sync.",
        ],
        { relatedId: "calendar", relatedLabel: "Calendar" },
      ),
      article(
        "staff",
        "Staff",
        "People who work at the shop.",
        [
          "**[Open the dashboard](/mechanic-dashboard)** Open All Mechanics at This Shop.",
          "Each person is listed with Owner or mechanic, plus email and phone.",
          "An owner chooses Manage Team & Invitations. Under Invite Mechanic, enter the mechanic’s email and choose Send Invite. The shop has to be Active.",
          "The mechanic opens that invite and creates the account.",
          "**[Log in as a shop](/login?role=Mechanic)** That is how the mechanic signs in after the invite.",
          "Promote to Owner gives that person the owner role on this shop. Owners can open plans, invites, and calendar settings.",
        ],
        { relatedId: "shop-vs-owner", relatedLabel: "Shop login and car-owner login" },
      ),
    ],
  },
  {
    id: "plans",
    title: "Plans and billing",
    articles: [
      article(
        "summary-cart-card",
        "The summary, the cart, and the card on file",
        "Put a credit card on file before any purchase.",
        [
          "**[Log in as a shop](/login?role=Mechanic)** Plans and bills are for an owner. A mechanic does not open this page.",
          "**[Manage Your Subscription](/pricing)** The cart in the corner is the monthly total of what is selected. YOUR SUBSCRIPTION at the bottom is that same list before checkout.",
          "A credit card is on file before any purchase and before the shop sends quotes. When a card is missing, the button is Add payment method. The free plan can stay. A shop billed $0 still keeps a card on file.",
          "Checkout charges that card. Subscribe Now is the first purchase. Update Subscription applies a change. No Changes means the cart already matches the current plan.",
        ],
        { relatedId: "current-subscription", relatedLabel: "Your current subscription" },
      ),
      article(
        "add-change-remove",
        "Add, change, or remove a service",
        "Change what the shop pays for on the subscription screen.",
        [
          "**[Manage Your Subscription](/pricing)** Only an owner can change the plan.",
          "SUBSCRIPTION PLANS is choose one. Check a box to add a service under Visibility & Marketing or Shop Management. Clear the box to take it off the next checkout.",
          "AI Phone Assistant is Configure Voice plan, not a checkbox. That window is its own step.",
          "YOUR SUBSCRIPTION shows the change, and the cart in the corner shows the new monthly total. Update Subscription applies it.",
          "**[Open Mechanic Profile](/mechanic-profile)** The plan button opens Your Active Subscriptions. Remove takes a running service off at the end of the current period. Turning Voice Base off also releases the Voice number.",
        ],
        { relatedId: "voice-on-this-screen", relatedLabel: "AI Phone Assistant on this screen" },
      ),
      article(
        "voice-on-this-screen",
        "AI Phone Assistant on this screen",
        "The short Voice choice on Manage Your Subscription. Phone setup is under Autoshops Voice.",
        [
          "**[Manage Your Subscription](/pricing)** AI PHONE ASSISTANT shows the Voice subtotal and Configure Voice plan.",
          "If Configure Voice plan is not on the page, Voice is not open for this shop yet.",
          "In that window, turn on Voice Base and any extra. Calendar, recording, and text chat require Voice Base. Turning Voice Base off cancels those extras and releases the Voice number.",
          "Close the window. The cart includes the Voice subtotal. Subscribe Now or Update Subscription checks out with the card on file.",
          "The phone itself is set up after checkout. This page only buys the plan.",
        ],
        { relatedId: "subscribe-and-open", relatedLabel: "Subscribe and open Voice" },
      ),
      article(
        "visibility-on-this-screen",
        "Visibility and Marketing on this screen",
        "The short list on Manage Your Subscription. Each product is under Visibility & Marketing.",
        [
          "**[Manage Your Subscription](/pricing)** VISIBILITY & MARKETING lists Priority Listing, Featured Shop Badge, and Google Reviews Display when they are for sale.",
          "Check a box to add one. Clear the box to take it off. A row marked NO LONGER AVAILABLE cannot be selected again after it is removed.",
          "YOUR SUBSCRIPTION and the cart show the monthly price. Update Subscription applies the change.",
          "What each product does for customers is in Visibility & Marketing.",
        ],
        { relatedId: "priority-listing", relatedLabel: "Priority Listing" },
      ),
      article(
        "current-subscription",
        "Your current subscription",
        "What the shop is paying for now, and where the bills are.",
        [
          "**[Open Mechanic Profile](/mechanic-profile)** The plan button shows Free Plan, one plan name, or a count of Active Plans. Open it for Your Active Subscriptions. Next billing is on each plan.",
          "View History on that window is the record of plan changes. It is not the monthly bill.",
          "Monthly Statement is the bill. Statement month lists the current month, the accruing month, and past months.",
          "Accruing means the total is still changing. It is not final until the month closes. The summary lists subscriptions, platform service fees, and AI voice answering when the shop has Voice.",
          "View full invoice opens the summary and the detail pages. The print icon saves a PDF. Statement history lists past months. Click a month to open it.",
          "More than one shop uses Billing location to pick which shop’s bill this is.",
          "**[Send a message](/contact?subject=Billing%20question)** Use this link when a charge looks wrong. Ask a question on this page does the same thing.",
        ],
        { relatedId: "add-change-remove", relatedLabel: "Add, change, or remove a service" },
      ),
    ],
  },
  {
    id: "voice",
    title: "Autoshops Voice",
    articles: [
      article(
        "what-you-buy",
        "What it is and what you buy",
        "The AI phone for the shop, and the monthly prices.",
        [
          "Autoshops Voice answers the shop’s phone. You buy it on autoshops.com. The phone itself is set up on voice.autoshops.ai.",
          "Voice Base is $39 a month. It includes a new Voice number, call answering, a written summary, a text transcript, and the shared autoshops.com calendar. It does not include a playable recording or any minutes.",
          "Calendar upgrade is $25 a month. It connects Google, Outlook, or CalDAV, and shop software such as Tekmetric, Shopmonkey, or Mitchell. Shop software is the same $25. It requires Voice Base.",
          "Call recording is $25 a month. It plays the audio of a call. Summaries and transcripts are already in Voice Base. It requires Voice Base.",
          "SMS Text Chat is $15 a month. Customers text the Voice number. It requires Voice Base.",
          "Calendar, recording, and Text Chat cannot be bought without Voice Base.",
        ],
      ),
      article(
        "subscribe-and-open",
        "Subscribe and open Voice",
        "Buy Voice on autoshops.com, then open the phone setup.",
        [
          "Sign in with the shop login. A car-owner login cannot buy Voice.",
          "On Manage Your Subscription, put a credit card on file before you buy. A card is required before any purchase, not only Voice.",
          "Open Pricing and choose Configure Voice plan. If that choice is not on the screen, Voice is not open for this shop yet.",
          "Choose Voice Base and any add-on, then check out. Checkout uses the card already on file.",
          "Active Plans lists AI Phone Assistant after checkout.",
          "Open the phone setup from Mechanic Profile → Open Voice Assistant, or from Active Plans → Complete Voice setup. Both land in the same place.",
          "You arrive on voice.autoshops.ai. Accept the invite if it asks. Do not register on a different Voice site, and do not pay again.",
          "Any owner on the shop can open Voice. It is not limited to one email.",
        ],
      ),
      article(
        "reach-the-shop",
        "How customers reach the shop",
        "Advertise the Voice number, or forward the number customers already call.",
        [
          "After the Voice number exists, Active Plans is where you keep this choice.",
          "Advertise this number when you want customers to dial the Voice number itself. Put it on Google and on the shop’s website. Autoshops does not edit those places.",
          "Forward the shop’s existing number when customers should keep dialing the number they already know. You turn that on with the phone company or office phone system, and you leave it on. Autoshops does not log in and do it. An ordinary shop phone line can do this.",
          "The setup to use is no-answer after 3 to 6 rings, and busy, left on 24 hours a day, 7 days a week. During open hours the shop can still pick up. If nobody answers, the assistant does. After hours the shop phone still rings those few rings, then the assistant answers.",
          "Forward every call only when the assistant should answer every time. Then the shop phone does not ring at all.",
          "You can also turn that every-call forward on and off yourself each day. At closing, turn it on so the assistant answers. At opening, turn it off so the shop phone rings. For example, on at 5:00 and off at 7:00. The 3-to-6-ring forward left on all week covers those same calls without that daily step.",
          "Forwarding moves the call. It does not ring the shop and Voice at the same time.",
          "A voice forward does not forward texts. A text to the old shop number stays on that line.",
        ],
        { relatedId: "forward-setup", relatedLabel: "How to turn on forwarding" },
      ),
      article(
        "forward-setup",
        "How to turn on forwarding",
        "Leave no-answer forwarding on. These are the lines shops in the Orlando area often have.",
        [
          "Use the Voice number from Active Plans as the number you forward to. Match the example to the shop’s line, then leave that setting on.",
          "**[Verizon cell](https://www.verizon.com/support/call-forwarding-faqs/)** For the usual setup, dial *71 and the 10-digit Voice number. Unanswered and busy calls go to Voice after about 3 to 6 rings. Dial *72 and the Voice number only when every call should skip the cell.",
          "**[AT&T Phone for Business](https://www.att.com/support/smallbusiness/article/smb-local-long-distance/KM1181691/)** Dial *92, the 10-digit Voice number, then #. Dial *90, the Voice number, then # so a busy line forwards too. If the account has a ring count, set it from 3 to 6. Dial *72, the Voice number, then # only when every call should skip the shop phone.",
          "**[Spectrum Business Voice](https://enterprise.spectrum.com/content/dam/spectrum/enterprise/en/pdfs/support/user-guides/spectrum-business-voice-user-guide.pdf)** No-answer and busy are set in the account, not with a star code. Sign in to Spectrum Business Voice Manager, open the line, and turn on Call Forward No Answer and Call Forward Busy to the Voice number. Set no-answer to about 3 to 6 rings. If voicemail is still the no-answer destination, change it or voicemail keeps the call. Dial *72 and the Voice number only when every call should skip the shop phone. Dial *73 to turn that every-call forward off.",
          "**[T-Mobile cell](https://www.t-mobile.com/support/plans-features/self-service-short-codes)** Dial **61*1, the 10-digit Voice number, then **15# and press call. Fifteen seconds is about 3 rings. Use **30# instead of **15# for about 6 rings. Dial **67*1, the Voice number, then # so a busy line forwards too.",
          "**[RingCentral](https://support.ringcentral.com/article-v2/Setting-up-how-incoming-calls-are-handled.html?brand=RingCentral&language=en_US&product=RingEX)** Open Settings, then Phone, then Call handling. Ring the shop phones in order for about 3 to 6 rings. Under missed calls, forward to the Voice number. Use the same rule for work hours and after hours, or one schedule that covers the whole week. To skip the assistant during the day, set after hours to forward to the Voice number and work hours to ring the shop.",
          "To turn the assistant on only when you close, use the every-call forward and turn it off when you open. For example, on at 5:00 and off at 7:00. Verizon: dial *72 and the Voice number to turn it on, and *73 to turn it off. AT&T: dial *72, the Voice number, then # to turn it on, and *73# to turn it off. Spectrum Business Voice: dial *72 and the Voice number to turn it on, and *73 to turn it off. T-Mobile: dial **21*1, the Voice number, then # to turn it on, and ##21# to turn it off.",
          "Each company name opens that company’s own instructions. The steps here were taken from those pages on October 19, 2026. A company can change them after that date.",
        ],
        { relatedId: "local-phone-lines", relatedLabel: "Local phone companies" },
      ),
      article(
        "local-phone-lines",
        "Local phone companies",
        "Summit Broadband and Brightspeed, the local lines around Orlando.",
        [
          "**[Summit Broadband](https://support.summit-broadband.com/hc/en-us/articles/360044561134-How-do-I-enable-call-forwarding)** The local fiber phone company in Orange, Osceola, Seminole, and Lake counties. On a Summit digital phone, turn voicemail off before you set no-answer or busy forwarding, or voicemail keeps the call.",
          "For the usual setup, dial *92 and the 10-digit Voice number right away. That sends unanswered calls to Voice after the number of rings that line already uses. Dial *90 and the Voice number so a busy line forwards too. Dial *93 to turn no-answer off, and *91 to turn busy off.",
          "To send every call, or to turn the assistant on at closing, dial *72 and the Voice number with no pause. Dial *73 to turn it off at opening. From another phone in Central Florida, dial 407-996-4723, then the shop’s 10-digit number and PIN, then *72 and the Voice number. Dial *73 there to turn it off.",
          "**[Brightspeed](https://www.brightspeed.com/help/home-phone/calling-features/use-call-forwarding-to-send-your-calls-to-another-phone/)** The local phone company on many older business lines around Orlando. Those lines were CenturyLink. For the usual setup, dial *92 and the Voice number for no-answer, and *90 and the Voice number for busy. Dial *93 and *91 to turn those off.",
          "To send every call on a Brightspeed landline, or to turn the assistant on at closing, dial *72, wait for the second tone, then dial the Voice number. If that call is not answered, hang up and repeat *72 and the number within two minutes. Dial *73 to turn it off at opening. On Brightspeed Digital Voice, those same codes ask for the voicemail PIN first. Then press 1, enter the Voice number, and press #.",
          "Each company name opens that company’s own instructions. The steps here were taken from those pages on October 19, 2026. A company can change them after that date.",
        ],
      ),
      article(
        "greeting-hours-faq-transfer",
        "Greeting, hours, FAQs, and transfer",
        "What to set on voice.autoshops.ai before customers call.",
        [
          "Confirm the Voice number is showing.",
          "Set the greeting: the shop name and what callers hear first.",
          "Set open hours on Voice. Hours saved on autoshops.com do not copy over. Match them to the phone system. Open times use the shop’s timezone.",
          "Add FAQs for hours, address, what you work on, and walk-ins. Do not put prices or warranties in the FAQs. The assistant should say to ask the shop.",
          "Call the Voice number once from a cell. Confirm the greeting, and confirm the call shows in the list.",
        ],
        { relatedId: "transfer-and-screening", relatedLabel: "Transfer lines and call screening" },
      ),
      article(
        "transfer-and-screening",
        "Transfer lines and call screening",
        "Who the assistant can dial, when, and whether it asks first.",
        [
          "On voice.autoshops.ai open Configuration → Phone numbers → Transfer calls. Add a row for each person, department, or service.",
          "Enter a name, a phone number, and a short description. Use a cell or another service that does not forward into Voice. Sending the call back to the number that forwards in loops: shop, Voice, shop, Voice.",
          "Mark one row as the default. That is who the assistant dials when the caller asks for a person and does not name one.",
          "Screen first puts the caller on hold. A second call shows this line’s number, not the caller’s number, and asks yes or no. Yes connects. No, no answer, or voicemail sends the caller back to leave a message.",
          "Transfer directly connects without that check. Use Transfer directly for another AI assistant or another service. Screen first treats that answer as a missed person and will not connect the caller.",
          "Leave limit transfer hours off when this row should be dialable any time the line itself may transfer. The line’s open hours still apply. While the line is closed, the assistant takes a message and does not dial.",
          "Turn limit transfer hours on to add a window for that row only. The assistant dials only inside those hours. Outside them, it does not dial that row.",
          "For a handoff that should cover almost the whole day, set that row to Transfer directly and set its hours to 23 hours and 59 minutes. Set the line’s open hours wide enough to cover the same day, or the assistant still will not transfer while the line is closed.",
        ],
      ),
      article(
        "after-a-call",
        "After a call",
        "Where the summary, transcript, and recording are.",
        [
          "Open the call list on voice.autoshops.ai.",
          "Every call has a written summary and a text transcript.",
          "Playable audio is there only when Call recording is on.",
          "Someone at the shop checks the call list each morning.",
        ],
      ),
      article(
        "direct-request",
        "A call that starts a Direct request",
        "The call can start a request. It does not book the visit or quote a price.",
        [
          "The assistant only offers times the shop calendar says are open, in the shop’s timezone.",
          "For a caller who already has an account, the call can start a Direct service request: which car, which service from the shop’s catalog, a preferred open time, and whether drop-off works.",
          "Drop-off is yes or no, then which day or days. The assistant does not ask for a clock time to drop the car off. The shop’s open hours supply when they can drop by and when they can pick up.",
          "The request shows under Direct requests on the shop dashboard. Quote it the same way as a request that started on the website.",
          "The call does not confirm an appointment and does not quote a price. The calendar row is written after the shop sends the quote and the customer accepts.",
          "The customer email says the request is waiting on the shop. It does not say the visit is booked.",
          "A caller with no account gets an email invite. After they join, the request is there to quote. If the phone matches more than one account, the assistant cannot attach the call to a customer.",
        ],
        { relatedId: "after-a-call", relatedLabel: "After a call" },
      ),
      article(
        "texting",
        "Texting the Voice number",
        "Texts go to the Voice number. A voice forward does not carry texts.",
        [
          "Customers text the Voice number. This needs SMS Text Chat, and Text Chat needs Voice Base.",
          "Forwarding the old shop number for voice calls does not forward texts. A text to the old number is not this product.",
        ],
      ),
      article(
        "pause-and-cancel",
        "Pause and cancel",
        "Pause keeps the number and stops the charge. Cancel releases the number.",
        [
          "Pause keeps the Voice number and stops the charge while the line is paused. That is why a shop pauses.",
          "A pause can run 3 months at a time, and 3 months total in any 12 months. In the last allowed month the owner is emailed.",
          "If you want the same number back, contact Autoshops before that last month ends. There is no Reinstate button to teach yet. If you do not come back, the number is cancelled and released.",
          "Cancel is Active Plans → Remove on AI Phone Assistant, or Configure Voice plan → turn Voice Base off. Either way asks you to confirm.",
          "Cancel also cancels Calendar, Call recording, and Text Chat, and it releases the Voice number.",
          "After cancel, remove the forward on the old shop number so callers are not sent to a number you no longer have.",
          "Pause and Cancel are different. Remove is not how you keep the number.",
        ],
      ),
    ],
  },
  {
    id: "visibility",
    title: "Visibility & Marketing",
    articles: [
      article(
        "priority-listing",
        "Priority Listing",
        "Be seen first when a customer is choosing a shop.",
        [
          "A customer opens Shops Near Me and picks from a list. Priority Listing moves this shop up that list, ahead of shops that do not have it. The value is being seen while they are deciding.",
          "This is the order of the list. It does not put a mark on the card, and it does not show the shop’s reviews.",
          "**[Shops Near Me](/google-wrapper)** This is the list customers use. With Priority Listing, the shop sits at the top of these search results.",
          "**[Open the dashboard](/mechanic-dashboard)** Shop Overview shows a Priority mark while it is on.",
          "**[Manage Your Subscription](/pricing)** Under VISIBILITY & MARKETING, check Priority Listing, then Update Subscription. The monthly price is on that row.",
          "**[Open Mechanic Profile](/mechanic-profile)** Remove takes it off at the end of the current period. The shop then sits with the shops that have not bought it.",
        ],
        { relatedId: "featured-badge", relatedLabel: "Featured Shop Badge" },
      ),
      article(
        "featured-badge",
        "Featured Shop Badge",
        "A mark on the shop card so customers notice this shop.",
        [
          "Priority Listing changes where the shop sits in the list. The Featured badge changes the card itself. Customers see a featured corner on this shop while they scroll. The value is being recognized, not only listed.",
          "**[Shops Near Me](/google-wrapper)** That corner is the featured mark on the shop card, and the shop is highlighted in the results.",
          "**[Open the dashboard](/mechanic-dashboard)** Shop Overview shows the Featured badge while it is on.",
          "**[Manage Your Subscription](/pricing)** Under VISIBILITY & MARKETING, check the Featured badge, then Update Subscription. The monthly price is on that row.",
          "**[Open Mechanic Profile](/mechanic-profile)** Remove takes it off at the end of the current period. The corner leaves the card.",
        ],
        { relatedId: "google-reviews", relatedLabel: "Google Reviews Display" },
      ),
      article(
        "google-reviews",
        "Google Reviews Display",
        "Show the shop’s Google reviews before a customer picks a shop.",
        [
          "These are reviews the shop already earned on Google. Google Reviews Display puts them on the Autoshops home page, where a customer is deciding whether to trust a shop. The value is that proof.",
          "Autoshops shows the reviews. It does not write them. A shop with strong reviews shows that strength. A shop with few reviews, or poor ones, shows those too.",
          "The shop does not reply to a Google review from this site. A reply still belongs on Google.",
          "**[Open the home page](/)** Customers see those reviews on the home page.",
          "**[Open the dashboard](/mechanic-dashboard)** The shop’s review list marks them Google Review. There is no reply on a Google review.",
          "**[Manage Your Subscription](/pricing)** Under VISIBILITY & MARKETING, check Google Reviews, then Update Subscription. The monthly price is on that row.",
          "**[Send a message](/contact?subject=Google%20reviews)** Use this link if the reviews are for the wrong business. Ask a question on this page does the same thing.",
        ],
        { relatedId: "visibility-on-this-screen", relatedLabel: "Visibility and Marketing on this screen" },
      ),
    ],
  },
];

export function findArticle(articleId) {
  for (const topic of HELP_TOPICS) {
    const article = topic.articles.find((item) => item.id === articleId);
    if (article) return { topic, article };
  }
  return null;
}

export function articlesForTopic(topicId) {
  const topic = HELP_TOPICS.find((item) => item.id === topicId) || HELP_TOPICS[0];
  return topic.articles.map((article) => ({ topic, article }));
}

export function searchHelp(query) {
  const words = query.trim().toLowerCase();
  if (!words) return null;
  const hits = [];
  for (const topic of HELP_TOPICS) {
    for (const article of topic.articles) {
      const haystack = [topic.title, article.title, article.summary, ...article.steps]
        .join(" ")
        .toLowerCase();
      if (haystack.includes(words)) hits.push({ topic, article });
    }
  }
  return hits;
}

export function signedInContact() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user || typeof user !== "object") return { name: "", email: "" };
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return { name, email: typeof user.email === "string" ? user.email : "" };
  } catch {
    return { name: "", email: "" };
  }
}

export function contactHref(articleTitle) {
  const { name, email } = signedInContact();
  const params = new URLSearchParams();
  params.set("subject", `Help: ${articleTitle}`);
  if (name) params.set("name", name);
  if (email) params.set("email", email);
  return `/contact?${params.toString()}`;
}
